/**
 * 购物车页专用脚本：
 * - cart-page：加减 / 移除 / 编辑用 Section Rendering 换清单；优惠码只补价格、折扣和标签，不换行项
 * - cart-selection：行项勾选 / 全选、按勾选项重算合计与结账件数、结账入口；赠品锁定并随正价勾选显隐
 * - 部分结账会把未勾选商品暂存到 themeCartDeferred，结账完成或返回后再加回
 * - cart-page-promo：优惠码应用（抽屉里的同名逻辑挂在 cart-drawer 上，购物车页不渲染抽屉，这里单独一份）
 */
(() => {
  const STORAGE_KEY = 'theme:cart-unselected-lines';
  const OVERLAY_ID = 'cart-page-loading';
  const OVERLAY_LOCK = 'is-cart-page-loading';
  let leaveLock = false;

  const mountGlobalLoadingIcon = (spinner) => {
    const template = document.getElementById('global-loading-icon-template');
    if (!template?.content) return;
    spinner.append(template.content.cloneNode(true));
  };

  const parseGiftPromoTitles = (root) => {
    try {
      return new Set(
        JSON.parse(root?.getAttribute('data-gift-promo-titles') || '[]').map((title) => String(title).toLowerCase())
      );
    } catch (error) {
      return new Set();
    }
  };

  const isGiftPromoCode = (code, root) => parseGiftPromoTitles(root).has(String(code || '').toLowerCase());

  const voucherLabel = (code) =>
    typeof window.__getPointsVoucherLabel === 'function' ? window.__getPointsVoucherLabel(code) : code;

  const isGiftLine = (item) => typeof window.__themeIsGiftLine === 'function' && window.__themeIsGiftLine(item);

  const readDosGiftOffer = () => {
    const node = document.getElementById('dos-gifts');
    if (!node) return null;

    let data = null;
    try {
      data = JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
    if (!data || !Array.isArray(data.e)) return null;

    const entries = data.e.filter((entry) => entry && entry.ok === 1);
    if (!entries.length) return null;

    const offer = entries[0];
    if (offer.t === 's') {
      let amt = Number(offer.amt);
      if (!(amt > 0) && offer.tiers && offer.tiers[0]) amt = Number(offer.tiers[0].thr);
      const rateFromShopify = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      const rate = isFinite(rateFromShopify) && rateFromShopify > 0 ? rateFromShopify : 1;
      const max = Math.ceil(Number((amt * rate).toFixed(2))) * 100;
      if (!(max > 0)) return { unknownThreshold: true };
      return { quantity: false, max: max };
    }

    let qty = Number(offer.qty);
    if (!(qty > 0) && offer.tiers && offer.tiers[0]) qty = Number(offer.tiers[0].thr);
    if (!(qty > 0)) return { unknownThreshold: true };
    return { quantity: true, max: qty };
  };

  /**
   * 勾选正价是否够满赠门槛。读不到 DOS 配置时返回 null，不误删赠品。
   */
  const paidSelectionMeetsGiftThreshold = (selection) => {
    const offer = readDosGiftOffer();
    if (!offer || offer.unknownThreshold || !(offer.max > 0)) return null;

    let spend = 0;
    let count = 0;
    selection.paidRows.forEach((row) => {
      const input = row.querySelector('[data-cart-select]');
      if (!input || !input.checked) return;
      spend += parseInt(row.getAttribute('data-original-price'), 10) || 0;
      count += parseInt(row.getAttribute('data-quantity'), 10) || 0;
    });

    return offer.quantity ? count >= offer.max : spend >= offer.max;
  };

  const collectGiftPromoTitles = (cart) => {
    const titles = [];
    const seen = new Set();

    (cart.items || []).forEach((item) => {
      if (!isGiftLine(item)) return;

      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const title = allocation.discount_application && allocation.discount_application.title;
        if (!title) return;
        const key = String(title).toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        titles.push(title);
      });
    });

    return titles;
  };

  const lineDiscountsFromItem = (item) => {
    const discounts = [];

    (item.line_level_discount_allocations || []).forEach((allocation) => {
      const title = allocation.discount_application && allocation.discount_application.title;
      if (!title || !allocation.amount) return;
      discounts.push({
        title: title,
        amount: allocation.amount,
        type: (allocation.discount_application && allocation.discount_application.type) || '',
      });
    });

    if (discounts.length) return discounts;

    (item.discounts || []).forEach((entry) => {
      if (!entry.title || !entry.amount) return;
      discounts.push({ title: entry.title, amount: entry.amount, type: entry.type || '' });
    });

    return discounts;
  };

  const formatMoney = (cents) => {
    if (typeof window.themeFormatMoney === 'function') return window.themeFormatMoney(cents);
    return String(Math.round(Number(cents) || 0) / 100);
  };

  const normalizeUnselected = (parsed) => {
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((entry) => {
        if (typeof entry === 'string') return { key: entry, variantId: '' };
        if (!entry || typeof entry !== 'object') return null;
        return {
          key: entry.key || '',
          variantId: entry.variantId ? String(entry.variantId) : '',
        };
      })
      .filter((entry) => entry && (entry.key || entry.variantId));
  };

  const readStore = () => {
    try {
      return normalizeUnselected(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]'));
    } catch (error) {
      return [];
    }
  };

  const writeStore = (entries) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
      /* 隐私模式下写不进去，忽略即可，退化为默认全选 */
    }
  };

  const setInputChecked = (input, checked) => {
    if (!input) return;
    input.checked = checked;
    if (checked) input.setAttribute('checked', '');
    else input.removeAttribute('checked');
  };

  const showCartPageLoading = (label) => {
    let overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.className = 'cart-page-loading';
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'polite');
      overlay.setAttribute('aria-hidden', 'true');
      const spinner = document.createElement('span');
      spinner.className = 'cart-page-loading__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      mountGlobalLoadingIcon(spinner);
      const text = document.createElement('span');
      text.className = 'sr-only';
      text.textContent = label || 'Loading';
      overlay.append(spinner, text);
      document.body.append(overlay);
    }
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(OVERLAY_LOCK);
  };

  const hideCartPageLoading = (force) => {
    if (leaveLock && !force) return;
    leaveLock = false;
    const overlay = document.getElementById(OVERLAY_ID);
    document.documentElement.classList.remove(OVERLAY_LOCK);
    if (!overlay) return;
    overlay.classList.remove('is-visible', 'is-leave');
    overlay.setAttribute('aria-hidden', 'true');
  };

  const startCartPageLoading = () => {
    document.documentElement.dispatchEvent(new CustomEvent('theme:overlay-loading:start', { bubbles: true }));
  };

  const stopCartPageLoading = () => {
    document.documentElement.dispatchEvent(new CustomEvent('theme:overlay-loading:end', { bubbles: true }));
  };

  const publishCartPageChange = (cart, baseEvent) => {
    startCartPageLoading();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: baseEvent, cart: cart },
      })
    );
  };

  class CartPageController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.getAttribute('section-id') || '';
      this.swiper = null;
      this.ymalRetries = 0;
      this.floatingObserver = null;
      this.loadingTimer = 0;
      this.replaceGeneration = 0;
      this.lastChangeAt = 0;
      this.userActionPending = false;
      this.mqBelow1000 = window.matchMedia('(max-width: 999px)');

      this.onBundle = this.onBundle.bind(this);
      this.onVariantAdd = this.onVariantAdd.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
      this.onCartRefresh = this.onCartRefresh.bind(this);
      this.onLoadingStart = this.onLoadingStart.bind(this);
      this.onLoadingEnd = this.onLoadingEnd.bind(this);
      this.syncFloatingHeight = this.syncFloatingHeight.bind(this);
      this.placeAccelerated = this.placeAccelerated.bind(this);

      document.addEventListener('cart:prepare-bundled-sections', this.onBundle);
      document.addEventListener('variant:add', this.onVariantAdd);
      document.addEventListener('cart:change', this.onCartChange, { capture: true });
      document.addEventListener('cart:refresh', this.onCartRefresh);
      document.addEventListener('theme:overlay-loading:start', this.onLoadingStart);
      document.addEventListener('theme:overlay-loading:end', this.onLoadingEnd);
      window.addEventListener('pageshow', (event) => {
        const keepOverlay =
          !event.persisted &&
          window.themeCartDeferred &&
          typeof window.themeCartDeferred.shouldShowRestoreLoading === 'function' &&
          window.themeCartDeferred.shouldShowRestoreLoading();
        if (keepOverlay) return;
        hideCartPageLoading(true);
      });
      if (
        window.themeCartDeferred &&
        typeof window.themeCartDeferred.shouldShowRestoreLoading === 'function' &&
        window.themeCartDeferred.shouldShowRestoreLoading()
      ) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:overlay-loading:start', { bubbles: true }));
      }
      window.addEventListener('resize', this.syncFloatingHeight);
      if (typeof this.mqBelow1000.addEventListener === 'function') {
        this.mqBelow1000.addEventListener('change', this.placeAccelerated);
      }

      this.initUi();
    }

    ensureRoot() {
      if (this.root && this.root.isConnected) return true;
      const live = document.querySelector('[data-cart-page]');
      if (!live) return false;
      this.root = live;
      this.sectionId = live.getAttribute('section-id') || this.sectionId;
      return true;
    }

    onBundle(event) {
      if (this.sectionId) event.detail.sections.add(this.sectionId);
    }

    /* 分类页加购会拉开抽屉，FocusTrap 换层时顺带 hide。购物车页 cartType=page，
       抽屉不开，CHOOSE OPTIONS 的 quick-buy 会一直挂在 body 上。 */
    onVariantAdd(event) {
      const source = event.target;
      if (!(source instanceof Element)) return;
      const modal = source.closest('quick-buy-modal');
      if (modal && typeof modal.hide === 'function') modal.hide();
    }

    async onCartRefresh(event) {
      const detail = event && event.detail;
      const fromDos = detail && (typeof detail.added === 'number' || typeof detail.removed === 'number');
      if (this.userActionPending || window.__themeGiftSettlePending) return;
      if (Date.now() - this.lastChangeAt < 800) return;
      if (!this.ensureRoot()) return;
      const stayQuiet =
        window.themeCartDeferred &&
        typeof window.themeCartDeferred.shouldStayQuiet === 'function' &&
        window.themeCartDeferred.shouldStayQuiet();
      if (!fromDos && !stayQuiet) startCartPageLoading();
      await this.onCartChange({
        detail: { cart: null, baseEvent: fromDos ? 'discountos-gift' : 'cart:refresh' },
      });
    }

    onLoadingStart(event) {
      if (event && event.detail && event.detail.leave) {
        leaveLock = true;
      }
      const overlay = document.getElementById(OVERLAY_ID);
      const alreadyVisible = !!(overlay && overlay.classList.contains('is-visible'));
      this.userActionPending = true;
      this.lastChangeAt = Date.now();
      showCartPageLoading(this.root.getAttribute('data-loading-label'));
      const liveOverlay = document.getElementById(OVERLAY_ID);
      if (leaveLock && liveOverlay) liveOverlay.classList.add('is-leave');
      /* 赠品结算的 keepLoading 会反复 start，不能把 15 秒兜底一直往后推。 */
      if (!(alreadyVisible && window.__themeGiftSettlePending)) {
        window.clearTimeout(this.loadingTimer);
        this.loadingTimer = window.setTimeout(() => hideCartPageLoading(true), 15000);
      }
    }

    onLoadingEnd() {
      const empty = this.ensureRoot() && !!this.root.querySelector('.cart-page__empty');
      if (window.__themeGiftSettlePending && empty && typeof window.__themeAbortGiftSettle === 'function') {
        window.__themeAbortGiftSettle();
      }
      if (window.__themeGiftSettlePending) return;
      this.userActionPending = false;
      window.clearTimeout(this.loadingTimer);
      this.loadingTimer = 0;
      hideCartPageLoading();
    }

    async onCartChange(event) {
      this.lastChangeAt = Date.now();
      if (!this.ensureRoot()) return;

      const cart = event.detail && event.detail.cart;
      if (event.detail && event.detail.silent) {
        this.patchFromCart(cart);
        const selection = this.root.querySelector('cart-selection');
        if (selection && typeof selection.refreshRows === 'function') selection.refreshRows();
        return;
      }
      const quiet = Boolean(event.detail && event.detail.quiet);
      if (cart && typeof window.__themeIsGiftUnsettled === 'function' && window.__themeIsGiftUnsettled(cart)) {
        if (!quiet) {
          startCartPageLoading();
          return;
        }
      }

      const promoOnly = event.detail && event.detail.baseEvent === 'cart-page-promo';
      const generation = ++this.replaceGeneration;

      try {
        const replacedList = await this.replaceListFromCart(cart, generation);
        if (replacedList) {
          this.patchFromCart(cart);
          return;
        }

        if (promoOnly) {
          this.patchFromCart(cart);
          return;
        }

        await this.replaceFromCart(cart, generation);
      } catch (error) {
        /* 局部替换失败时关掉 overlay，避免页面一直锁住 */
      } finally {
        if (generation === this.replaceGeneration) this.onLoadingEnd();
      }
    }

    async replaceListFromCart(cart, generation) {
      let html = cart && cart.sections ? cart.sections[this.sectionId] : null;
      if (!html) return false;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionRoot = doc.getElementById(`shopify-section-${this.sectionId}`) || doc;
      const next = sectionRoot.querySelector('[data-cart-page]');
      const nextList = next && next.querySelector('.cart-page__list');
      const currentList = this.root.querySelector('.cart-page__list');
      if (!next || !nextList || !currentList || generation !== this.replaceGeneration) return false;

      const currentMain = this.root.querySelector('.cart-page__main');
      currentList.replaceWith(document.importNode(nextList, true));

      const currentSold = this.root.querySelector('.cart-page__sold-out');
      const nextSold = next.querySelector('.cart-page__sold-out');
      if (currentSold && nextSold) {
        currentSold.replaceWith(document.importNode(nextSold, true));
      } else if (currentSold) {
        currentSold.remove();
      } else if (nextSold && currentMain) {
        const ymal = currentMain.querySelector('[data-cart-ymal]');
        const imported = document.importNode(nextSold, true);
        if (ymal) currentMain.insertBefore(imported, ymal);
        else currentMain.appendChild(imported);
      }

      const currentTitle = this.root.querySelector('.cart-page__title');
      const nextTitle = next.querySelector('.cart-page__title');
      if (currentTitle && nextTitle) currentTitle.textContent = nextTitle.textContent;

      const inner = this.root.querySelector('.cart-page__inner') || this.root;
      inner.querySelectorAll('cart-edit-modal').forEach((node) => node.remove());
      next.querySelectorAll('cart-edit-modal').forEach((node) => {
        inner.appendChild(document.importNode(node, true));
      });

      const selection = this.root.querySelector('cart-selection');
      if (selection && typeof selection.refreshRows === 'function') selection.refreshRows();

      const currentYmal = this.root.querySelector('[data-cart-ymal]');
      const nextYmal = next.querySelector('[data-cart-ymal]');
      if (currentYmal && nextYmal && currentYmal.getAttribute('data-product-id') !== nextYmal.getAttribute('data-product-id')) {
        currentYmal.setAttribute('data-product-id', nextYmal.getAttribute('data-product-id') || '');
        if (nextYmal.hasAttribute('data-ymal-url')) {
          currentYmal.setAttribute('data-ymal-url', nextYmal.getAttribute('data-ymal-url'));
        } else {
          currentYmal.removeAttribute('data-ymal-url');
        }
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
        const pending = nextYmal.querySelector('[data-cart-ymal-pending]');
        if (pending) {
          currentYmal.replaceChildren(document.importNode(pending, true));
          currentYmal.removeAttribute('hidden');
        } else {
          currentYmal.replaceChildren();
          currentYmal.hidden = true;
        }
        this.resetYmalRequest();
        this.loadRecommendations();
      }

      return true;
    }

    patchFromCart(cart) {
      if (!cart) return;

      const selection = this.root.querySelector('cart-selection');
      if (selection && typeof selection.applyCartData === 'function') {
        selection.applyCartData(cart);
      }

      this.root.querySelectorAll('cart-page-promo').forEach((promo) => {
        if (typeof promo.syncTagsFromCart === 'function') promo.syncTagsFromCart(cart);
      });

      if (typeof window.__relabelPointsVoucherTitles === 'function') {
        window.__relabelPointsVoucherTitles(this.root);
      }

      this.syncFloatingHeight();
    }

    async replaceFromCart(cart, generation) {
      let html = cart && cart.sections ? cart.sections[this.sectionId] : null;

      if (!html) {
        const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`);
        if (!response.ok || generation !== this.replaceGeneration) return;
        html = await response.text();
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionRoot = doc.getElementById(`shopify-section-${this.sectionId}`) || doc;
      const next = sectionRoot.querySelector('[data-cart-page]');
      if (!next || generation !== this.replaceGeneration) return;

      this.root.classList.toggle('cart-page--empty', next.classList.contains('cart-page--empty'));

      const ymal = this.root.querySelector('[data-cart-ymal]');
      const preservedYmal = ymal && ymal.childElementCount ? ymal : null;
      if (preservedYmal) {
        preservedYmal.remove();
      } else if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      if (this.floatingObserver) {
        this.floatingObserver.disconnect();
        this.floatingObserver = null;
      }

      this.root.replaceChildren(
        ...Array.from(next.children).map((child) => document.importNode(child, true))
      );

      const newYmal = this.root.querySelector('[data-cart-ymal]');
      if (
        preservedYmal &&
        newYmal &&
        preservedYmal.getAttribute('data-product-id') === newYmal.getAttribute('data-product-id')
      ) {
        newYmal.replaceWith(preservedYmal);
      } else if (preservedYmal && this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.initUi();

      if (typeof window.__relabelPointsVoucherTitles === 'function') {
        window.__relabelPointsVoucherTitles(this.root);
      }
    }

    initUi() {
      this.bindFloating();
      this.placeAccelerated();
      this.initYmal();
      this.bindLeaveLinks();
    }

    bindLeaveLinks() {
      if (this._leaveBound) return;
      this._leaveBound = true;

      const startLeave = (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        const target = event.target instanceof Element ? event.target : event.target && event.target.parentElement;
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        const anchor =
          (target && target.closest && target.closest('a[href]')) ||
          path.find((node) => node && node.tagName === 'A' && node.hasAttribute && node.hasAttribute('href'));
        if (!anchor || !this.root.contains(anchor)) return;
        if (
          !anchor.matches(
            '.cart-page__empty-button, .cart-row__media, .cart-row__title, .product-card__title, .product-card__media-link, .product-card__cover'
          )
        ) {
          return;
        }
        if (/\/cart\/(change|update|add|clear)/.test(anchor.pathname || '')) return;
        leaveLock = true;
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:overlay-loading:start', { bubbles: true, detail: { leave: true } })
        );
      };

      this.root.addEventListener('pointerdown', startLeave, true);
      this.root.addEventListener('click', startLeave, true);
    }

    bindFloating() {
      const floating = this.root.querySelector('[data-cart-floating]');
      if (this.floatingObserver) this.floatingObserver.disconnect();
      if (floating && typeof ResizeObserver === 'function') {
        this.floatingObserver = new ResizeObserver(this.syncFloatingHeight);
        this.floatingObserver.observe(floating);
      }
      this.syncFloatingHeight();
    }

    syncFloatingHeight() {
      const floating = this.root.querySelector('[data-cart-floating]');
      if (!floating) {
        this.root.style.setProperty('--cart-floating-height', '0px');
        return;
      }
      const visible = window.getComputedStyle(floating).display !== 'none';
      this.root.style.setProperty('--cart-floating-height', visible ? `${floating.offsetHeight}px` : '0px');
    }

    placeAccelerated() {
      const accelerated = document.getElementById(`cart-accelerated-${this.sectionId}`);
      const slot = this.root.querySelector('[data-accelerated-slot]');
      const home = this.root.querySelector('.cart-page__actions');
      if (!accelerated || !slot || !home) return;

      if (this.mqBelow1000.matches) {
        if (accelerated.parentElement !== slot) slot.appendChild(accelerated);
      } else if (accelerated.parentElement !== home) {
        home.insertBefore(accelerated, home.querySelector('.cart-page__payment-icons'));
      }

      this.syncFloatingHeight();
    }

    initYmal() {
      const ymalHost = this.root.querySelector('[data-cart-ymal]');
      if (!ymalHost) return;

      const hasSlides = !!ymalHost.querySelector('[data-cart-ymal-swiper]');
      if (hasSlides) this.createSwiper();

      if (ymalHost.dataset.source === 'empty') {
        this.loadEmptyYmal({ refreshOnly: hasSlides });
        return;
      }
      this.loadRecommendations({ refreshOnly: hasSlides });
    }

    resetYmalRequest() {
      const boot = window.__cartYmal;
      if (!boot) return;
      boot.request = null;
      boot.url = '';
      boot.cached = '';
      boot.cacheKey = '';
    }

    ymalRecommendationsUrl(host) {
      const boot = window.__cartYmal;
      if (boot && boot.url) return boot.url;
      if (host.dataset.ymalUrl) return host.dataset.ymalUrl;
      return (
        Shopify.routes.root +
        'recommendations/products?section_id=cart-recommendations&intent=related&product_id=' +
        encodeURIComponent(host.dataset.productId) +
        '&limit=' +
        encodeURIComponent(host.dataset.limit || 10)
      );
    }

    async fetchYmalHtml(url) {
      const boot = window.__cartYmal;
      if (boot && boot.url === url && boot.request) return boot.request;

      const response = await fetch(url, { credentials: 'same-origin' });
      return { ok: response.ok, text: await response.text() };
    }

    writeYmalCache(html) {
      const key = window.__cartYmal && window.__cartYmal.cacheKey;
      if (!key || !html) return;
      try {
        sessionStorage.setItem(key, html);
        window.__cartYmal.cached = html;
      } catch (error) {
        /* 隐私模式写不进 sessionStorage 就跳过 */
      }
    }

    ymalProductSignature(root) {
      return Array.from(root.querySelectorAll('.product-card[data-product-id]'))
        .map((card) => card.getAttribute('data-product-id'))
        .join(',');
    }

    hideYmalIfPending(host) {
      if (!host || host.querySelector('[data-cart-ymal-swiper]')) return;
      host.hidden = true;
      host.replaceChildren();
    }

    applyYmalHtml(host, html, options = {}) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const block = doc.querySelector('.cart-ymal');
      if (!block) {
        if (!options.refreshOnly) this.hideYmalIfPending(host);
        return false;
      }

      this.writeYmalCache(block.outerHTML);
      if (options.refreshOnly && this.ymalProductSignature(host) === this.ymalProductSignature(block)) {
        return true;
      }
      return this.showYmalBlock(host, block);
    }

    showYmalBlock(host, block) {
      if (!host || !block) return false;
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      host.replaceChildren(document.importNode(block, true));
      host.removeAttribute('hidden');
      this.ymalRetries = 0;
      this.createSwiper();
      return true;
    }

    async loadEmptyYmal(options = {}) {
      const ymalHost = this.root.querySelector('[data-cart-ymal]');
      if (!ymalHost) return;

      const fallbackTemplate = ymalHost.querySelector('[data-cart-ymal-fallback]');
      const showFallback = () => {
        const block = fallbackTemplate && fallbackTemplate.content.querySelector('.cart-ymal');
        return this.showYmalBlock(ymalHost, block);
      };

      let ids = [];
      try {
        ids = JSON.parse(window.localStorage.getItem('theme:recently-viewed-products') || '[]');
      } catch (error) {
        ids = [];
      }
      if (!Array.isArray(ids)) ids = [];

      const limit = parseInt(ymalHost.dataset.limit, 10) || 10;
      const query = ids
        .slice(0, limit)
        .map((id) => 'id:' + id)
        .join(' OR ');

      if (!query) {
        if (!options.refreshOnly) showFallback() || this.hideYmalIfPending(ymalHost);
        return;
      }

      try {
        const url =
          Shopify.routes.root +
          'search?type=product&q=' +
          encodeURIComponent(query) +
          '&section_id=cart-recommendations';
        const result = await this.fetchYmalHtml(url);
        if (!result.ok || !this.applyYmalHtml(ymalHost, result.text, options)) {
          if (!options.refreshOnly) showFallback() || this.hideYmalIfPending(ymalHost);
        }
      } catch (error) {
        if (!options.refreshOnly) showFallback() || this.hideYmalIfPending(ymalHost);
      }
    }

    createSwiper() {
      const ymalHost = this.root.querySelector('[data-cart-ymal]');
      if (!ymalHost) return;

      const root = ymalHost.querySelector('[data-cart-ymal-swiper]');
      const prevEl = ymalHost.querySelector('[data-cart-ymal-prev]');
      const nextEl = ymalHost.querySelector('[data-cart-ymal-next]');
      if (!root || this.swiper) return;

      const navButtons = [prevEl, nextEl].filter(Boolean);

      if (root.querySelectorAll('.swiper-slide').length < 2) {
        navButtons.forEach((button) => {
          button.hidden = true;
        });
        return;
      }

      if (typeof Swiper === 'undefined') {
        if (this.ymalRetries < 40) {
          this.ymalRetries += 1;
          window.setTimeout(() => this.createSwiper(), 50);
        }
        return;
      }

      this.swiper = new Swiper(root, {
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        spaceBetween: 4,
        loop: false,
        watchOverflow: true,
        resistanceRatio: 0.85,
        navigation: { prevEl: prevEl, nextEl: nextEl },
        mousewheel: { forceToAxis: true, releaseOnEdges: true },
      });
    }

    async loadRecommendations(options = {}) {
      const ymalHost = this.root.querySelector('[data-cart-ymal]');
      if (!ymalHost) return;

      try {
        const result = await this.fetchYmalHtml(this.ymalRecommendationsUrl(ymalHost));
        if (!result.ok) {
          if (!options.refreshOnly) this.hideYmalIfPending(ymalHost);
          return;
        }
        this.applyYmalHtml(ymalHost, result.text, options);
      } catch (error) {
        if (!options.refreshOnly) this.hideYmalIfPending(ymalHost);
      }
    }
  }

  if (!window.customElements.get('cart-selection')) {
    class CartSelection extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        this.rows = Array.from(this.querySelectorAll('[data-cart-line]'));
        this.selectAllInputs = Array.from(this.querySelectorAll('[data-cart-select-all]'));
        this.checkoutButtons = Array.from(this.querySelectorAll('.cart-page__checkout'));
        this.formId = this.getAttribute('form-id');

        try {
          this.cartLevelDiscounts = JSON.parse(this.getAttribute('cart-level-discounts') || '[]');
        } catch (error) {
          this.cartLevelDiscounts = [];
        }

        this.restoreSelection();

        this.addEventListener('change', (event) => {
          const target = event.target;

          if (target.matches('[data-cart-select-all]')) {
            this.setAll(target.checked);
            return;
          }

          if (target.matches('[data-cart-select]')) {
            if (target.closest('[data-gift-line]')) {
              this.render();
              return;
            }

            this.persist();
            this.render();
          }
        });

        this.checkoutButtons.forEach((button) => {
          button.addEventListener('click', this.onCheckout.bind(this));
        });

        this.render();
      }

      get paidRows() {
        return this.rows.filter((row) => !row.hasAttribute('data-gift-line'));
      }

      get giftRows() {
        return this.rows.filter((row) => row.hasAttribute('data-gift-line'));
      }

      get paidInputs() {
        return this.paidRows.map((row) => row.querySelector('[data-cart-select]')).filter(Boolean);
      }

      restoreSelection() {
        if (typeof window.__themeHydrateCartSelection === 'function') {
          window.__themeHydrateCartSelection(this);
          return;
        }
        this.applyUnselected(readStore());
      }

      refreshRows() {
        this.rows = Array.from(this.querySelectorAll('[data-cart-line]'));
        this.selectAllInputs = Array.from(this.querySelectorAll('[data-cart-select-all]'));
        this.checkoutButtons = Array.from(this.querySelectorAll('.cart-page__checkout'));
        this.restoreSelection();
        this.render();
      }

      applyCartData(cart) {
        const items = cart.items || [];

        this.rows.forEach((row) => {
          const item = items.find((entry) => entry.key === row.getAttribute('data-line-key'));
          if (!item) return;
          this.applyItemToRow(row, item);
        });

        const cartLevel = (cart.cart_level_discount_applications || []).map((entry) => ({
          title: entry.title,
          amount: entry.total_allocated_amount,
        }));
        this.cartLevelDiscounts = cartLevel;
        this.setAttribute('cart-level-discounts', JSON.stringify(cartLevel));

        const giftTitles = collectGiftPromoTitles(cart);
        this.setAttribute('data-gift-promo-titles', JSON.stringify(giftTitles));
        this.querySelectorAll('cart-page-promo').forEach((promo) => {
          promo.setAttribute('data-gift-promo-titles', JSON.stringify(giftTitles));
        });

        this.render();
      }

      applyItemToRow(row, item) {
        const quantity = Number(item.quantity) || parseInt(row.getAttribute('data-quantity'), 10) || 0;
        const original = Number(item.original_line_price) || Number(item.original_price) * quantity || 0;
        const lineDiscount = Number(item.line_level_total_discount) || 0;
        const fromLinePrice = Number(item.line_price);
        const fallbackFinal =
          Number.isFinite(fromLinePrice) && fromLinePrice > 0
            ? fromLinePrice
            : Math.max(original - lineDiscount, 0);
        // cart/update.js 偶发不带 final_line_price，或优惠码写回后给 0；赠品才该是 0
        const rawFinal = item.final_line_price;
        const parsedFinal = rawFinal == null ? fallbackFinal : Number(rawFinal) || 0;
        const finalPrice =
          parsedFinal > 0 || original === 0 || row.hasAttribute('data-gift-line')
            ? parsedFinal
            : fallbackFinal;
        const discounts = lineDiscountsFromItem(item);
        const discountTotal = discounts.reduce((sum, entry) => sum + (entry.amount || 0), 0);

        row.setAttribute('data-original-price', original);
        row.setAttribute('data-final-price', finalPrice);
        row.setAttribute('data-line-discount', discountTotal);
        row.setAttribute('data-line-discounts', JSON.stringify(discounts));

        const priceValue = row.querySelector('.cart-row__price-value');
        if (priceValue) {
          priceValue.textContent = formatMoney(finalPrice);
          priceValue.classList.toggle('cart-row__price-value--on-sale', original > finalPrice);
        }

        const priceHost = row.querySelector('.cart-row__price');
        let compare = row.querySelector('.cart-row__price-compare');
        if (original > finalPrice) {
          if (!compare && priceHost && priceValue) {
            compare = document.createElement('s');
            compare.className = 'cart-row__price-compare';
            priceValue.after(compare);
          }
          if (compare) compare.textContent = formatMoney(original);
        } else if (compare) {
          compare.remove();
        }
      }

      applyUnselected(unselected) {
        const unused = unselected.slice();

        this.paidRows.forEach((row) => {
          const input = row.querySelector('[data-cart-select]');
          if (!input) return;

          const key = row.getAttribute('data-line-key');
          const variantId = row.getAttribute('data-variant-id');
          let index = unused.findIndex((entry) => entry.key && entry.key === key);
          if (index === -1 && variantId) {
            index = unused.findIndex((entry) => entry.variantId && entry.variantId === variantId);
          }

          setInputChecked(input, index === -1);
          if (index !== -1) unused.splice(index, 1);
        });
      }

      persist() {
        const unselected = this.paidRows
          .filter((row) => {
            const input = row.querySelector('[data-cart-select]');
            return input && !input.checked;
          })
          .map((row) => ({
            key: row.getAttribute('data-line-key') || '',
            variantId: row.getAttribute('data-variant-id') || '',
          }));

        writeStore(unselected);
      }

      setAll(checked) {
        this.paidInputs.forEach((input) => {
          setInputChecked(input, checked);
        });
        this.persist();
        this.render();
      }

      /**
       * 赠品和优惠码标签跟着正价勾选走：至少勾了一件正价就留下赠品行，
       * 勾选金额够满赠门槛才自动勾上；正价全不勾则藏赠品行和可见优惠码标签。
       * 读不到门槛时沿用「有正价就勾上」，避免误把已达标赠品摘掉。
       */
      syncDependentOffers() {
        const anyPaidSelected = this.paidInputs.some((input) => input.checked);
        const giftEligible = paidSelectionMeetsGiftThreshold(this);
        const checkGifts = anyPaidSelected && giftEligible !== false;

        this.giftRows.forEach((row) => {
          const input = row.querySelector('[data-cart-select]');
          if (input) setInputChecked(input, checkGifts);
          row.hidden = !anyPaidSelected;
        });

        this.toggleAttribute('data-has-paid-selection', anyPaidSelected);

        this.querySelectorAll('cart-page-promo').forEach((promo) => {
          if (typeof promo.syncVoucherTags === 'function') promo.syncVoucherTags();
        });
      }

      get selectedRows() {
        return this.rows.filter((row) => {
          const input = row.querySelector('[data-cart-select]');
          return input && input.checked;
        });
      }

      /**
       * 合计口径与全选时的服务端值对齐：
       * Subtotal 取折前行价合计（不含赠品），Discount 为「勾选正价行的商品级折扣 + 全选时才算的整车折扣」，
       * 商品合计 = Subtotal − Discount。Total 再由 themeCartShipping 加上当前运费。
       * 赠品不进小计、也不进优惠。
       * 全选时包邮门槛跟商品合计同一口径，避免优惠码写回后 final 丢字段、或整车折扣被减两次。
       * 部分勾选时不猜优惠码 / 满减门槛：整车券和 discount_code 一律不计入 Discount，
       * 运费门槛只用勾选行现价（final），结账时由 Shopify 按整车重算。
       */
      isSharedCoupon(entry) {
        if (String(entry.type || '').toLowerCase() === 'discount_code') return true;
        const title = String(entry.title || '').toLowerCase();
        if (!title) return false;
        return this.cartLevelDiscounts.some((item) => String(item.title || '').toLowerCase() === title);
      }

      compute() {
        const selected = this.selectedRows;
        const paidSelectedCount = this.paidInputs.filter((input) => input.checked).length;
        const allSelected = this.paidRows.length > 0 && paidSelectedCount === this.paidRows.length;

        let subtotal = 0;
        let itemCount = 0;
        let shippingQualifying = 0;
        const discounts = [];

        const pushDiscount = (title, amount) => {
          if (!amount) return;
          const existing = discounts.find((entry) => entry.title === title);
          if (existing) {
            existing.amount += amount;
          } else {
            discounts.push({ title: title, amount: amount });
          }
        };

        const lineEntriesOf = (row) => {
          try {
            return JSON.parse(row.getAttribute('data-line-discounts') || '[]');
          } catch (error) {
            return [];
          }
        };

        selected.forEach((row) => {
          itemCount += parseInt(row.getAttribute('data-quantity'), 10) || 0;
          if (row.hasAttribute('data-gift-line')) return;

          const original = parseInt(row.getAttribute('data-original-price'), 10) || 0;
          subtotal += original;

          let countedLineDiscount = 0;
          lineEntriesOf(row).forEach((entry) => {
            if (!allSelected && this.isSharedCoupon(entry)) return;
            pushDiscount(entry.title, entry.amount);
            countedLineDiscount += entry.amount || 0;
          });

          if (row.getAttribute('data-requires-shipping') !== 'false') {
            const finalAttr = parseInt(row.getAttribute('data-final-price'), 10);
            const payableFromOriginal = Math.max(original - countedLineDiscount, 0);
            if (Number.isFinite(finalAttr) && (finalAttr > 0 || original === 0)) {
              shippingQualifying += finalAttr;
            } else {
              shippingQualifying += payableFromOriginal;
            }
          }
        });

        if (allSelected) {
          this.cartLevelDiscounts.forEach((entry) => {
            const already = discounts.some(
              (item) => String(item.title || '').toLowerCase() === String(entry.title || '').toLowerCase()
            );
            if (already) return;
            pushDiscount(entry.title, entry.amount);
            shippingQualifying -= entry.amount;
          });
        }

        const discountTotal = discounts.reduce((sum, entry) => sum + entry.amount, 0);
        const total = Math.max(subtotal - discountTotal, 0);
        const paidSelected = selected.filter((row) => !row.hasAttribute('data-gift-line'));
        const shippingSelected = paidSelected.filter(
          (row) => row.getAttribute('data-requires-shipping') !== 'false'
        );

        // 全选且勾选行都要运费时，包邮门槛跟商品合计同一口径
        if (allSelected && shippingSelected.length === paidSelected.length) {
          shippingQualifying = total;
        }

        return {
          allSelected: allSelected,
          selectedCount: paidSelectedCount,
          itemCount: itemCount,
          subtotal: subtotal,
          discounts: discounts,
          discountTotal: discountTotal,
          shippingQualifying: Math.max(shippingQualifying, 0),
          total: total,
        };
      }

      render() {
        this.syncDependentOffers();
        const state = this.compute();
        this.removeAttribute('data-selection-booting');
        this.removeAttribute('data-totals-pending');

        this.selectAllInputs.forEach((input) => {
          input.checked = state.allSelected;
          input.indeterminate = !state.allSelected && state.selectedCount > 0;
        });

        this.querySelectorAll('[data-cart-subtotal]').forEach((node) => {
          node.textContent = formatMoney(state.subtotal);
        });

        this.querySelectorAll('[data-cart-total]').forEach((node) => {
          node.setAttribute('data-cart-merchandise', String(state.total));
          node.textContent = formatMoney(state.total);
        });

        this.querySelectorAll('[data-cart-discount-total]').forEach((node) => {
          node.textContent = '-' + formatMoney(state.discountTotal);
        });

        this.querySelectorAll('[data-cart-savings]').forEach((node) => {
          node.textContent = formatMoney(state.discountTotal);
        });

        this.querySelectorAll('[data-cart-discount-block]').forEach((node) => {
          node.hidden = state.discountTotal === 0;
        });

        this.querySelectorAll('[data-cart-discount-list]').forEach((list) => {
          list.innerHTML = state.discounts
            .filter((entry) => !isGiftPromoCode(entry.title, this))
            .map(
              (entry) =>
                '<div class="cart-summary__row cart-summary__row--discount">' +
                '<span class="cart-summary__label" data-discount-code="' +
                this.escape(entry.title) +
                '">' +
                this.escape(voucherLabel(entry.title)) +
                '</span>' +
                '<span class="cart-summary__value">-' +
                this.escape(formatMoney(entry.amount)) +
                '</span>' +
                '</div>'
            )
            .join('');
        });

        this.querySelectorAll('[data-cart-count]').forEach((node) => {
          node.textContent = state.itemCount;
        });

        this.checkoutButtons.forEach((button) => {
          button.disabled = state.selectedCount === 0;
        });

        document.dispatchEvent(
          new CustomEvent('theme:cart-selection', {
            bubbles: true,
            detail: {
              shippingQualifying: state.shippingQualifying,
              selectedKeys: this.selectedRows
                .map((row) => row.getAttribute('data-line-key'))
                .filter(Boolean),
            },
          })
        );

        if (window.themeCartShipping && typeof window.themeCartShipping.sync === 'function') {
          window.themeCartShipping.sync(state.shippingQualifying);
        }
      }

      escape(value) {
        return String(value).replace(/[&<>"']/g, (character) => {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
        });
      }

      submitForm() {
        const form = this.formId ? document.getElementById(this.formId) : null;
        if (!form) return;

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '1';
        form.appendChild(checkoutInput);
        form.submit();
      }

      /**
       * Shopify 的结账始终带上整车行项，没有「只结算一部分」的原生入口。
       * 所以部分勾选时先把未勾选行从购物车移除，再提交，保证收费金额与页面显示一致。
       * 移除前把未勾选正价行完整快照下来，结账完成或从收银台返回后再加回。
       */
      async onCheckout(event) {
        if (paidSelectionMeetsGiftThreshold(this) === false) {
          this.giftRows.forEach((row) => {
            const input = row.querySelector('[data-cart-select]');
            if (input) setInputChecked(input, false);
          });
        }

        const state = this.compute();

        if (state.selectedCount === 0) {
          event.preventDefault();
          return;
        }

        if (state.allSelected) {
          if (window.themeCartDeferred) window.themeCartDeferred.clear();
          return;
        }

        event.preventDefault();

        const button = event.currentTarget;
        button.setAttribute('aria-busy', 'true');

        const dropGifts = paidSelectionMeetsGiftThreshold(this) === false;
        const updates = {};
        const deferredItems = [];
        const unselected = [];
        const deferred = window.themeCartDeferred;

        let cart = null;
        try {
          const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
          if (!cartResponse.ok) throw new Error('cart fetch failed');
          cart = await cartResponse.json();
        } catch (error) {
          button.removeAttribute('aria-busy');
          return;
        }

        const cartItems = cart.items || [];

        this.rows.forEach((row) => {
          const key = row.getAttribute('data-line-key');
          const input = row.querySelector('[data-cart-select]');
          if (!key) return;
          if (!((input && !input.checked) || (dropGifts && row.hasAttribute('data-gift-line')))) return;

          updates[key] = 0;
          if (row.hasAttribute('data-gift-line')) return;

          const item = cartItems.find((entry) => entry.key === key);
          const payload = item && deferred ? deferred.payloadFromCartItem(item) : null;
          if (payload) deferredItems.push(payload);
          unselected.push({
            key: key,
            variantId: String((item && item.variant_id) || row.getAttribute('data-variant-id') || ''),
          });
        });

        try {
          if (deferred) deferred.markLeaving();

          const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates }),
          });

          if (!response.ok) throw new Error('cart update failed');

          writeStore(unselected);
          if (deferred) deferred.save(deferredItems, { cartToken: cart.token });
          this.submitForm();
        } catch (error) {
          if (deferred) deferred.clearLeaving();
          button.removeAttribute('aria-busy');
        }
      }
    }

    window.customElements.define('cart-selection', CartSelection);
  }

  if (!window.customElements.get('cart-page-promo')) {
    const promoDetailsOpen = {};

    class CartPagePromo extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        this.field = this.querySelector('[data-promo-field]');
        this.input = this.querySelector('.input');
        this.applyButton = this.querySelector('[data-promo-apply]');
        this.errorEl = this.querySelector('[data-promo-error]');
        this.toastEl = this.closest('.cart-promo-block')?.querySelector('[data-promo-toast]');
        this.busy = false;
        this.restorePromoDetails();

        if (this.applyButton) this.applyButton.addEventListener('click', this.onApply.bind(this));

        if (this.input) {
          this.input.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            this.onApply();
          });
          this.input.addEventListener('input', this.clearError.bind(this));
        }

        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-promo-remove]');
          if (button) this.onRemove(button.getAttribute('data-code'));
        });

        this.syncVoucherTags();
      }

      restorePromoDetails() {
        const details = this.closest('details.cart-promo-block');
        if (!details) return;

        const variant = details.classList.contains('cart-promo-block--floating') ? 'floating' : 'aside';
        if (typeof promoDetailsOpen[variant] === 'boolean') details.open = promoDetailsOpen[variant];
        details.addEventListener('toggle', () => {
          promoDetailsOpen[variant] = details.open;
        });
      }

      get appliedCodes() {
        return Array.from(this.querySelectorAll('[data-promo-remove]')).map((button) =>
          button.getAttribute('data-code')
        );
      }

      giftConflictLabel() {
        return this.dataset.giftLabel || Array.from(parseGiftPromoTitles(this))[0] || '';
      }

      // 先看已渲染的赠品折扣标题和行项，都没命中再拉一次 cart.js 兜底，并顺手把属性补上
      async hasGiftInCart() {
        if (parseGiftPromoTitles(this).size > 0) return true;

        const scope = this.closest('[data-cart-page]') || document;
        if (scope.querySelector('[data-gift-line]')) return true;

        try {
          const cart = await fetch(`${Shopify.routes.root}cart.js`).then((response) => response.json());
          const titles = collectGiftPromoTitles(cart);
          if (titles.length) {
            const json = JSON.stringify(titles);
            this.setAttribute('data-gift-promo-titles', json);
            const selection = this.closest('cart-selection');
            if (selection) selection.setAttribute('data-gift-promo-titles', json);
          }
          return (cart.items || []).some(isGiftLine);
        } catch (error) {
          return false;
        }
      }

      showError(message) {
        if (this.errorEl) {
          this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        }
        if (this.field) this.field.classList.add('is-error');
      }

      showToast(type, codes) {
        if (!this.toastEl) return;

        const messageKey =
          type === 'voucher'
            ? 'voucherApplied'
            : type === 'failed'
              ? 'applicationFailed'
              : type === 'replaced'
                ? 'codesReplaced'
                : 'codeApplied';
        this.toastEl.textContent = (this.toastEl.dataset[messageKey] || '').replace(
          '__CODE__',
          (codes || []).map((code) => voucherLabel(code)).join(', ')
        );
        this.toastEl.hidden = false;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => {
          this.toastEl.hidden = true;
        }, 2500);
      }

      clearError() {
        if (this.errorEl) this.errorEl.hidden = true;
        const voucherError = this.querySelector('[data-points-voucher-error]');
        if (voucherError) voucherError.hidden = true;
        if (this.field) this.field.classList.remove('is-error');
      }

      setBusy(state) {
        this.busy = state;
        if (this.applyButton) this.applyButton.disabled = state;
        this.setAttribute('aria-busy', state ? 'true' : 'false');
      }

      get voucherCodes() {
        const voucherList = this.querySelector('cart-points-vouchers');
        const fromChild = voucherList?.voucherCodes || [];
        if (fromChild.length) return fromChild.map((code) => String(code).toLowerCase());
        if (typeof window.__getPointsVoucherCodes === 'function') return window.__getPointsVoucherCodes();
        return [];
      }

      isVoucherCode(code) {
        return this.voucherCodes.includes(String(code).toLowerCase());
      }

      // 普通优惠码一律追加，能否叠加由 Shopify 的折扣组合规则决定；
      // 积分券按设计稿是单选，换券时只摘掉已用的其它券
      getCodesForApplication(code) {
        if (!this.isVoucherCode(code)) return this.appliedCodes.concat([code]);

        const voucherCodes = this.voucherCodes;
        const retained = this.appliedCodes.filter((applied) => !voucherCodes.includes(applied.toLowerCase()));

        return retained.concat([code]);
      }

      // 已应用的积分券在下方 Points Voucher 区回显，上方标签只留作提交时的数据源。
      // 券码还没到时先别露出标签，避免积分券在上方闪一帧再被藏掉。
      syncVoucherTags() {
        const voucherCodes = this.voucherCodes;
        const known =
          typeof window.__hasKnownPointsVoucherCodes === 'function' && window.__hasKnownPointsVoucherCodes();
        const hasVoucherList = !!this.querySelector('cart-points-vouchers');
        let visibleCount = 0;

        const selection = this.closest('cart-selection');
        const hideForSelection = !!selection && !selection.hasAttribute('data-has-paid-selection');

        this.querySelectorAll('.cart-promo__tag').forEach((tag) => {
          const code = tag.querySelector('[data-promo-remove]')?.getAttribute('data-code') || '';
          const isVoucher = voucherCodes.includes(code.toLowerCase());
          const isGiftPromo = tag.hasAttribute('data-gift-promo') || isGiftPromoCode(code, this);

          tag.hidden = isVoucher || isGiftPromo || hideForSelection;
          if (!isVoucher && !isGiftPromo && !hideForSelection) visibleCount += 1;
        });

        const tagList = this.querySelector('[data-promo-tags]');
        if (tagList) tagList.hidden = visibleCount === 0;
        if (known || !hasVoucherList) this.classList.add('is-tags-ready');
      }

      syncTagsFromCart(cart) {
        const tagList = this.querySelector('[data-promo-tags]');
        if (!tagList) return;

        const codes = (cart.discount_codes || [])
          .filter((entry) => entry.code && entry.applicable !== false)
          .map((entry) => entry.code);

        const existing = new Map();
        this.querySelectorAll('.cart-promo__tag').forEach((tag) => {
          const code = tag.querySelector('[data-promo-remove]')?.getAttribute('data-code') || '';
          if (code) existing.set(code.toLowerCase(), tag);
        });

        const keep = new Set();
        codes.forEach((code) => {
          const key = code.toLowerCase();
          keep.add(key);
          const isGift = isGiftPromoCode(code, this);
          const tag = existing.get(key);
          if (tag) {
            const button = tag.querySelector('[data-promo-remove]');
            if (button) button.setAttribute('data-code', code);
            const label = tag.querySelector('.cart-promo__tag-code');
            if (label) {
              label.textContent = voucherLabel(code);
              label.setAttribute('data-discount-code', code);
            }
            tag.toggleAttribute('data-gift-promo', isGift);
            return;
          }

          tagList.appendChild(this.createTag(code, isGift));
        });

        existing.forEach((tag, key) => {
          if (!keep.has(key)) tag.remove();
        });

        this.syncVoucherTags();

        // 购物车页不重渲染券列表，选中态和可点状态跟着标签回写；右栏、悬浮栏两份都靠这里同步
        const voucherList = this.querySelector('cart-points-vouchers');
        if (typeof voucherList?.restoreSelection === 'function') voucherList.restoreSelection(this);
      }

      createTag(code, isGift) {
        const template = this.querySelector('[data-promo-tag-template]');
        const tag = template.content.firstElementChild.cloneNode(true);
        tag.toggleAttribute('data-gift-promo', !!isGift);
        tag.hidden = !!isGift;

        const label = tag.querySelector('.cart-promo__tag-code');
        label.textContent = voucherLabel(code);
        label.setAttribute('data-discount-code', code);
        tag.querySelector('[data-promo-remove]').setAttribute('data-code', code);

        return tag;
      }

      // Shopify 对「码不存在 / 已过期 / 不满足门槛」和「不能与现有码组合」一律只返回
      // applicable: false，把新码单独提交一次才能区分；探测完再把原有码提交回去还原购物车。
      // 旧码不止一个时定位不到冲突对象，探测没有意义
      async findConflictingCode(code, companionCodes) {
        if (companionCodes.length !== 1) return null;

        const probe = await this.submit([code]);
        let appliesAlone = false;

        if (probe.ok && probe.json) {
          const entry = (probe.json.discount_codes || []).find(
            (item) => item.code && item.code.toLowerCase() === code.toLowerCase()
          );
          appliesAlone = !!entry && entry.applicable !== false;
        }

        await this.submit(companionCodes);
        return appliesAlone ? companionCodes[0] : null;
      }

      showApplicationFailure(conflictingCode, fromVoucherList) {
        const message =
          conflictingCode && this.dataset.msgCombination
            ? this.dataset.msgCombination.replace('__CODE__', voucherLabel(conflictingCode))
            : this.dataset.msgError;

        const voucherError = this.querySelector('[data-points-voucher-error]');
        if (fromVoucherList && voucherError) {
          voucherError.textContent = message;
          voucherError.hidden = false;
        } else {
          this.showError(message);
        }
        this.showToast('failed');
      }

      async onApply() {
        if (this.busy || !this.input) return;

        const code = this.input.value.trim();
        if (!code) return;

        await this.applyCode(code, false);
      }

      // fromVoucherList 只决定错误提示落在哪块区域，是否按积分券处理一律看 code 本身
      async applyCode(code, fromVoucherList) {
        if (this.busy || !code) return false;

        const already = this.appliedCodes.some((applied) => applied.toLowerCase() === code.toLowerCase());
        if (already) {
          if (fromVoucherList) {
            const voucherError = this.querySelector('[data-points-voucher-error]');
            if (voucherError) {
              voucherError.textContent = this.dataset.msgAlready;
              voucherError.hidden = false;
            } else {
              this.showError(this.dataset.msgAlready);
            }
          } else {
            this.showError(this.dataset.msgAlready);
          }
          // 点券的 pointerdown 已经把转圈盖上了，这里不往下走就必须自己关掉
          stopCartPageLoading();
          return false;
        }

        this.clearError();

        // 从积分列表点的也算积分券，不依赖 voucherCodes 是否已经挂上。
        // 点下去转圈已经开了；有赠品就只提示、不请求，并立刻收掉转圈。setBusy(false) 不会关转圈。
        const treatAsVoucher = fromVoucherList || this.isVoucherCode(code);
        if (treatAsVoucher) {
          this.setBusy(true);
          const blockedByGift = await this.hasGiftInCart();
          if (blockedByGift) {
            this.showApplicationFailure(this.giftConflictLabel(), fromVoucherList);
            stopCartPageLoading();
            this.setBusy(false);
            return false;
          }
          this.setBusy(false);
        }

        this.setBusy(true);
        startCartPageLoading();

        const isVoucher = this.isVoucherCode(code);
        const submittedCodes = this.getCodesForApplication(code);
        const companionCodes = submittedCodes.filter(
          (submitted) => submitted.toLowerCase() !== code.toLowerCase()
        );
        const result = await this.submit(submittedCodes);
        const codes = result.ok && result.json ? result.json.discount_codes || [] : null;
        const match = codes
          ? codes.find((entry) => entry.code && entry.code.toLowerCase() === code.toLowerCase())
          : null;

        if (!codes || !match || match.applicable === false) {
          const giftCompanion = companionCodes.find((companion) => isGiftPromoCode(companion, this));
          const conflictingCode = giftCompanion
            ? this.giftConflictLabel()
            : await this.findConflictingCode(code, companionCodes);
          this.setBusy(false);
          stopCartPageLoading();
          this.showApplicationFailure(conflictingCode, fromVoucherList);
          return false;
        }

        // 新码生效但一起提交的旧码被 Shopify 丢掉时要说明，否则用户只看到静默替换
        const applicableCodes = codes
          .filter((entry) => entry.code && entry.applicable !== false)
          .map((entry) => entry.code.toLowerCase());
        const droppedCodes = companionCodes.filter(
          (companion) => !applicableCodes.includes(companion.toLowerCase())
        );

        this.setBusy(false);
        if (this.input) this.input.value = '';
        this.showToast(droppedCodes.length > 0 ? 'replaced' : isVoucher ? 'voucher' : 'code', droppedCodes);
        publishCartPageChange(result.json, 'cart-page-promo');
        return true;
      }

      async onRemove(code) {
        if (this.busy || !code || isGiftPromoCode(code, this)) return false;

        this.setBusy(true);
        startCartPageLoading();
        const remaining = this.appliedCodes.filter((applied) => applied.toLowerCase() !== code.toLowerCase());
        const result = await this.submit(remaining);
        if (!result.ok || !result.json) {
          this.setBusy(false);
          stopCartPageLoading();
          return false;
        }
        this.setBusy(false);
        publishCartPageChange(result.json, 'cart-page-promo');
        return true;
      }

      async submit(codes) {
        try {
          const sections = new Set();
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
          );
          const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              discount: codes.join(','),
              sections: Array.from(sections).join(','),
            }),
          });

          if (!response.ok) return { ok: false, json: null };
          return { ok: true, json: await response.json() };
        } catch (error) {
          return { ok: false, json: null };
        }
      }
    }

    window.customElements.define('cart-page-promo', CartPagePromo);
  }

  const cartPageRoot = document.querySelector('[data-cart-page]');
  if (cartPageRoot) new CartPageController(cartPageRoot);
})();
